import React from 'react';
import './ProgressDots.css';

export type DotRating = 'hard' | 'medium' | 'easy' | 'skip' | 'unanswered';
export type DotShape = 'circle' | 'square' | 'star' | 'triangle';

export interface ProgressDot {
  /** Unique identifier */
  id: string;
  /** Rating given to this card */
  rating: DotRating;
  /** Number of times this card has been reviewed (affects veteran styling) */
  reviewCount?: number;
  /** Shape of the dot (encodes selection reason) */
  shape?: DotShape;
}

export interface LegendItem {
  /** Label text */
  label: string;
  /** Rating for coloring */
  rating?: DotRating;
  /** Shape variant */
  shape?: DotShape;
  /** Whether to show as past (filled) or upcoming (dimmed) */
  isPast?: boolean;
  /** Whether to show as current */
  isCurrent?: boolean;
}

export interface LegendSection {
  /** Section title */
  title: string;
  /** Items in this section */
  items: LegendItem[];
}

export interface ProgressDotsProps {
  /** Array of dots representing cards in the session */
  dots: ProgressDot[];
  /** Index of the current card (0-based) */
  currentIndex: number;
  /** Maximum visible dots (will scroll to keep current centered) */
  maxVisible?: number;
  /** Legend sections to display */
  legend?: LegendSection[];
  /** Additional CSS class */
  className?: string;
}

/**
 * Render a single dot (used in both main display and legend)
 */
const Dot: React.FC<{
  rating: DotRating;
  shape?: DotShape;
  isCurrent?: boolean;
  isPast?: boolean;
  isVeteran?: boolean;
  isFirstVisible?: boolean;
  isLastVisible?: boolean;
  ariaLabel?: string;
}> = ({
  rating,
  shape = 'circle',
  isCurrent,
  isPast,
  isVeteran,
  isFirstVisible,
  isLastVisible,
  ariaLabel,
}) => (
  <div
    className={[
      'stc-progress-dot',
      `stc-progress-dot--${rating}`,
      `stc-progress-dot--shape-${shape}`,
      isCurrent && 'stc-progress-dot--current',
      isPast && 'stc-progress-dot--past',
      isVeteran && 'stc-progress-dot--veteran',
      isFirstVisible && 'stc-progress-dot--edge-left',
      isLastVisible && 'stc-progress-dot--edge-right',
    ]
      .filter(Boolean)
      .join(' ')}
    aria-label={ariaLabel}
  />
);

/**
 * Progress dots component showing session position and difficulty.
 * Based on DES-033 - dots carry emotional weight based on ratings.
 * Supports shape encoding for selection reasons.
 */
export const ProgressDots: React.FC<ProgressDotsProps> = ({
  dots,
  currentIndex,
  maxVisible = 15,
  legend,
  className = '',
}) => {
  // Calculate visible range to keep current dot centered
  const halfVisible = Math.floor(maxVisible / 2);
  let startIndex = Math.max(0, currentIndex - halfVisible);
  const endIndex = Math.min(dots.length, startIndex + maxVisible);

  // Adjust start if we're near the end
  if (endIndex - startIndex < maxVisible) {
    startIndex = Math.max(0, endIndex - maxVisible);
  }

  const visibleDots = dots.slice(startIndex, endIndex);
  const hasMoreLeft = startIndex > 0;
  const hasMoreRight = endIndex < dots.length;

  return (
    <div className={`stc-progress-dots-wrapper ${className}`}>
      <div
        className="stc-progress-dots"
        role="progressbar"
        aria-valuenow={currentIndex + 1}
        aria-valuemin={1}
        aria-valuemax={dots.length}
        aria-label={`Card ${currentIndex + 1} of ${dots.length}`}
      >
        <div className="stc-progress-dots__container">
          {visibleDots.map((dot, index) => {
            const actualIndex = startIndex + index;
            const isCurrent = actualIndex === currentIndex;
            const isPast = actualIndex < currentIndex;
            const isVeteran = (dot.reviewCount ?? 0) >= 50;
            const isFirstVisible = index === 0 && hasMoreLeft;
            const isLastVisible = index === visibleDots.length - 1 && hasMoreRight;

            return (
              <Dot
                key={dot.id}
                rating={dot.rating}
                shape={dot.shape}
                isCurrent={isCurrent}
                isPast={isPast}
                isVeteran={isVeteran}
                isFirstVisible={isFirstVisible}
                isLastVisible={isLastVisible}
                ariaLabel={`Card ${actualIndex + 1}: ${dot.rating}${isVeteran ? ' (veteran)' : ''}`}
              />
            );
          })}
        </div>
      </div>

      {legend && legend.length > 0 && (
        <div className="stc-progress-legend">
          {legend.map((section, sectionIndex) => (
            <div key={sectionIndex} className="stc-progress-legend__section">
              <div className="stc-progress-legend__title">{section.title}</div>
              <div className="stc-progress-legend__items">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="stc-progress-legend__item">
                    <Dot
                      rating={item.rating ?? 'unanswered'}
                      shape={item.shape}
                      isCurrent={item.isCurrent}
                      isPast={item.isPast}
                    />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
